/**
 * @file components/TokenCreateModal.jsx
 * @description Modal dialog for creating or editing an API token.
 *
 * Create mode: full plaintext token is shown once after creation, copy-to-clipboard provided.
 * Edit mode (pass `editToken`): pre-fills metadata and calls updateToken — the secret value
 * itself is never editable or re-shown here (see TokenRevealModal for regeneration).
 *
 * @param {Object}    props
 * @param {boolean}   props.show       - Whether the modal is visible.
 * @param {Function}  props.onClose    - Callback invoked when the modal is dismissed.
 * @param {Function}  props.onCreated  - Callback invoked after a successful create/update.
 * @param {Object}    [props.editToken] - Existing token to edit; omit to create a new one.
 */

import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createToken, updateToken } from '../api/tokens';

/** Initial state for the creation form. */
const EMPTY_FORM = {
  name:          '',
  description:   '',
  scopes:        '',   // comma-separated string → converted to array on submit
  allowed_paths: '',   // newline-separated path prefixes → converted to array on submit
  expires_at:    '',
};

/**
 * Modal for creating or editing an API token.
 * In create mode, displays the plaintext token once after successful creation.
 */
export default function TokenCreateModal({ show, onClose, onCreated, editToken }) {
  const queryClient = useQueryClient();
  const isEditMode   = Boolean(editToken);

  /** Form field values */
  const [form, setForm] = useState(EMPTY_FORM);

  /** Holds the plaintext token value returned by the API after creation */
  const [createdToken, setCreatedToken] = useState(null);

  /** Whether the user has copied the token to clipboard */
  const [copied, setCopied] = useState(false);

  // Pre-fill the form whenever we open in edit mode for a given token
  useEffect(() => {
    if (isEditMode && show) {
      setForm({
        name:          editToken.name,
        description:   editToken.description || '',
        scopes:        (editToken.scopes || []).join(', '),
        allowed_paths: (editToken.allowed_paths || []).join('\n'),
        // datetime-local expects 'YYYY-MM-DDTHH:mm'
        expires_at:    editToken.expires_at ? editToken.expires_at.slice(0, 16) : '',
      });
    } else if (!show) {
      setForm(EMPTY_FORM);
    }
  }, [isEditMode, editToken, show]);

  const mutation = useMutation({
    mutationFn: (payload) =>
      isEditMode ? updateToken(editToken.id, payload) : createToken(payload),
    onSuccess: (data) => {
      // Only create mode returns a one-time plaintext token to reveal
      if (!isEditMode) setCreatedToken(data.token);
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      onCreated?.();
    },
  });

  /** Update a single form field by name. */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /** Submits the form and calls the API. */
  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert comma-separated scopes string to a trimmed, non-empty array
    const scopes = form.scopes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // Parse allowed_paths: one path per line, stripped and filtered
    const allowed_paths = form.allowed_paths
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);

    mutation.mutate({
      name:          form.name,
      description:   form.description || undefined,
      scopes,
      allowed_paths,
      expires_at:    form.expires_at || null,
    });
  };

  /** Copies the token to the clipboard and shows a brief confirmation. */
  const handleCopy = () => {
    navigator.clipboard.writeText(createdToken).then(() => {
      setCopied(true);
      // Reset the "Copied!" indicator after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    });
  };

  /**
   * Resets all local state and calls the parent close handler.
   * Clears the token from memory so it cannot be viewed after dismissal.
   */
  const handleClose = () => {
    setForm(EMPTY_FORM);
    setCreatedToken(null);
    setCopied(false);
    mutation.reset();
    onClose();
  };

  if (!show) return null;

  return (
    // Bootstrap modal using inline display + backdrop
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-token-title"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">

            {/* Header */}
            <div
              className="modal-header"
              style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
            >
              <h5 className="modal-title" id="create-token-title">
                <i className="bi bi-key-fill me-2" aria-hidden="true" />
                {isEditMode ? 'Edit API Token' : 'Create API Token'}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* ── One-time token reveal ─────────────────────────────── */}
              {createdToken ? (
                <div>
                  <div className="alert alert-success d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-check-circle-fill fs-5" aria-hidden="true" />
                    <strong>Token created!</strong> Copy it now — it will not be shown again.
                  </div>

                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control font-monospace"
                      readOnly
                      value={createdToken}
                      aria-label="Generated token value"
                    />
                    <button
                      className="btn"
                      style={{
                        backgroundColor: copied ? 'var(--color-accent-2)' : 'var(--color-accent-1)',
                        color: '#000',
                      }}
                      type="button"
                      onClick={handleCopy}
                      aria-label="Copy token to clipboard"
                    >
                      <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} aria-hidden="true" />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <p className="text-muted small">
                    <i className="bi bi-shield-lock me-1" aria-hidden="true" />
                    Store this token securely. It has been hashed and cannot be recovered from the server.
                  </p>
                </div>
              ) : (
                /* ── Creation form ──────────────────────────────────── */
                <form id="token-form" onSubmit={handleSubmit} noValidate>
                  {/* Error banner */}
                  {mutation.isError && (
                    <div className="alert alert-danger" role="alert">
                      {mutation.error?.message || `Failed to ${isEditMode ? 'update' : 'create'} token.`}
                    </div>
                  )}

                  {/* Name */}
                  <div className="mb-3">
                    <label htmlFor="token-name" className="form-label fw-semibold">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="token-name"
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g. My Integration Token"
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label htmlFor="token-desc" className="form-label fw-semibold">Description</label>
                    <textarea
                      id="token-desc"
                      className="form-control"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Optional — what is this token used for?"
                    />
                  </div>

                  {/* Scopes */}
                  <div className="mb-3">
                    <label htmlFor="token-scopes" className="form-label fw-semibold">Scopes</label>
                    <input
                      id="token-scopes"
                      type="text"
                      className="form-control"
                      name="scopes"
                      value={form.scopes}
                      onChange={handleChange}
                      placeholder="e.g. read:logs, write:data (comma-separated)"
                    />
                    <div className="form-text">Comma-separated list of permission scopes.</div>
                  </div>

                  {/* Allowed paths — controls which /api/ proxy routes this token can access */}
                  <div className="mb-3">
                    <label htmlFor="token-paths" className="form-label fw-semibold">
                      Allowed proxy paths
                    </label>
                    <textarea
                      id="token-paths"
                      className="form-control font-monospace"
                      name="allowed_paths"
                      value={form.allowed_paths}
                      onChange={handleChange}
                      rows={3}
                      placeholder={'/api/v1/myapi2\n/api/v1/myapi3/endpoint\n* (for full access)'}
                    />
                    <div className="form-text">
                      One path prefix per line. A token for <code>/api/v1/myapi2</code> also
                      grants access to all sub-paths. Use <code>*</code> for unrestricted access.
                    </div>
                  </div>

                  {/* Expiry */}
                  <div className="mb-3">
                    <label htmlFor="token-expiry" className="form-label fw-semibold">
                      Expiry date
                    </label>
                    <input
                      id="token-expiry"
                      type="datetime-local"
                      className="form-control"
                      name="expires_at"
                      value={form.expires_at}
                      onChange={handleChange}
                    />
                    <div className="form-text">Leave blank for a non-expiring token.</div>
                  </div>
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
              >
                {createdToken ? 'Done' : 'Cancel'}
              </button>

              {/* Only show Submit while the form is active (not after token reveal) */}
              {!createdToken && (
                <button
                  type="submit"
                  form="token-form"
                  className="btn"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
                  disabled={mutation.isPending || !form.name.trim()}
                >
                  {mutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      {isEditMode ? 'Saving…' : 'Creating…'}
                    </>
                  ) : (
                    <>
                      <i className={`bi ${isEditMode ? 'bi-check2-circle' : 'bi-plus-circle'} me-1`} aria-hidden="true" />
                      {isEditMode ? 'Save Changes' : 'Create Token'}
                    </>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div className="modal-backdrop fade show" onClick={handleClose} aria-hidden="true" />
    </>
  );
}
